import { consume, ContextType } from '@lit/context';
import { Task } from '@lit/task';
import { SlDialog } from '@shoelace-style/shoelace';
import { css, html, LitElement } from 'lit';
import { customElement, query, state } from 'lit/decorators.js';
import { api } from '../api.js';
import { contexts } from '../contexts.js';
import { renderSnapshots } from '../fragments.js';
import { PageProjectSuperadm } from '../page-project-superadm.js';
import { linkStyles } from './cssstyles.js';

/**
 * The snapshot counts from `serverInfo`, clickable to reveal which projects the
 * snapshots belong to. The breakdown is superuser-only, so it is fetched when
 * the dialog opens rather than carried on the `serverInfo` context that every
 * client polls.
 *
 * The list has no upper bound — a project's current snapshot is never reaped,
 * only evicted under memory pressure — so it lives in a dialog that can scroll
 * rather than inline next to the counts.
 */
@customElement('server-snapshots')
export class ServerSnapshots extends LitElement {
  @consume({ context: contexts.serverInfo, subscribe: true }) @state() private _serverInfo?: ContextType<typeof contexts.serverInfo>;

  @query('sl-dialog') private _dialog?: SlDialog;

  private _snapshotsTask = new Task(this, {
    args: () => [] as const,
    autoRun: false,
    task: async ([], { signal }) => {
      const snapshots = await api.administration.snapshotsInfo.GET(undefined, { signal });
      return [...snapshots].sort((a, b) => b.snapshots - a.snapshots);
    },
  });

  override render() {
    return html`
      <button class=linklike @click=${() => {
        this._dialog?.show();
        this._snapshotsTask.run();
      }}>${renderSnapshots(this._serverInfo)}</button>

      <sl-dialog label="Data Snapshots">
        ${this._snapshotsTask.render({
          pending: () => html`<sl-spinner></sl-spinner>`,
          error: () => html`<div class=empty>Failed to fetch snapshot breakdown.</div>`,
          complete: snapshots => {
            const total = snapshots.reduce((acc, entry) => acc + entry.snapshots, 0);
            return html`
              <div class=summary>
                <strong>${total}</strong> snapshot${total === 1 ? '' : 's'} across
                <strong>${snapshots.length}</strong> project${snapshots.length === 1 ? '' : 's'}
                on <span class=mono>${this._serverInfo?.hostname}</span>
              </div>
              ${snapshots.length ? html`
                <div class=list>
                  ${snapshots.map(entry => html`
                    <div class=row>
                      <a class=name href=${PageProjectSuperadm.url({ orgSlug: entry.orgSlug, projectSlug: entry.projectSlug, snapshotId: undefined })}>
                        ${entry.orgSlug}/${entry.projectSlug}
                      </a>
                      <sl-relative-time class=age sync date=${new Date(entry.lastAccessTimestamp).toISOString()}></sl-relative-time>
                      <strong>${entry.snapshots}</strong>
                    </div>
                  `)}
                </div>
              ` : html`
                <div class=empty>This instance is holding no data snapshots right now.</div>
              `}
            `;
          },
        })}
      </sl-dialog>
    `;
  }

  static styles = [linkStyles, css`
    :host {
      display: contents;
    }

    button.linklike {
      background: none;
      border: none;
      padding: 0;
      color: inherit;
      font: inherit;
      cursor: pointer;
      text-decoration: underline;
      text-decoration-style: dotted;
    }

    button.linklike:hover {
      text-decoration-style: solid;
    }

    .summary {
      color: var(--sl-color-neutral-600);
      margin-bottom: var(--sl-spacing-medium);
    }

    .row {
      display: flex;
      align-items: baseline;
      gap: var(--sl-spacing-medium);
      padding: var(--sl-spacing-2x-small) 0;
      border-bottom: 1px solid var(--sl-color-neutral-100);
    }

    .row:last-child {
      border-bottom: none;
    }

    .name {
      flex: auto;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .age {
      color: var(--sl-color-neutral-500);
      font-size: var(--sl-font-size-small);
      white-space: nowrap;
    }

    .mono {
      font-family: var(--sl-font-mono);
      font-size: var(--sl-font-size-small);
    }

    .empty {
      color: var(--sl-color-neutral-500);
    }
  `];
}
